import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useMessageForm } from '../../forms';

const slideIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(50%);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.75rem;
`;

const StyledSuggestion = styled.button<{ $delay: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.625rem 0.75rem;
  min-height: 2.75rem;
  font-size: 0.9rem;
  color: #023030;
  border: none;
  background-color: #c8f0f2;
  border-radius: 0.75rem;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  cursor: pointer;
  text-align: right;
  width: fit-content;
  max-width: 100%;
  max-height: 2.5rem;

  transition-property: color, background, box-shadow, transform;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;

  &:hover,
  &:focus {
    background: #a8e2e5;
    color: #063a3c;
    box-shadow: 0 2px 6px rgba(0, 48, 48, 0.15);
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    pointer-events: all;
    background-color: #e9e7e4;
    color: #a9a29d;
    box-shadow: none;
  }

  opacity: 0;
  animation: ${slideIn} 1s forwards;
  animation-delay: ${({ $delay }) => $delay};
`;

type Suggestion = {
  message: string;
  delay?: string;
};

type Props = {
  suggestions: Suggestion[];
};

export function SuggestedMessages({ suggestions }: Readonly<Props>) {
  const { setFieldValue, isValid, isSubmitting } = useMessageForm();

  const handleSelect = async (text: string) => {
    await setFieldValue('message', text);
  };

  return (
    <StyledList>
      {suggestions.map(({ message, delay = '0s' }) => (
        <StyledSuggestion
          key={message}
          type="button"
          $delay={delay}
          disabled={isSubmitting || isValid}
          onClick={() => handleSelect(message)}
        >
          {message}
        </StyledSuggestion>
      ))}
    </StyledList>
  );
}
